import {envIsDev, envIsTest} from '../../../../config';
import {datadogEnabled} from '../../datadog';
import {
  getRumViewPageState,
  type RumViewPageState,
} from '../../datadogRumViewVisibility';
import {MAX_PAIN_OBSERVATION_DURATION_MS} from './constants';
import {observeCriticalWorkflowPainWindow} from './observer';
import type {
  CriticalWorkflowContext,
  CriticalWorkflowObservationOptions,
} from './types';

export const AMBIENT_RESPONSIVENESS_WORKFLOW = 'ambient-responsiveness-sample';

const AMBIENT_RESPONSIVENESS_SAMPLER_VERSION = 1;
const BYTES_PER_MEBIBYTE = 1024 * 1024;
const MAX_TIMER_DELAY_MS = 2_147_483_647;
const MILLISECONDS_PER_SECOND = 1_000;

interface NetworkInformationSnapshot {
  downlink?: number;
  effectiveType?: string;
  rtt?: number;
  saveData?: boolean;
  type?: string;
}

interface NavigatorSnapshot {
  connection?: NetworkInformationSnapshot;
  deviceMemory?: number;
  onLine?: boolean;
}

interface PerformanceMemorySnapshot {
  jsHeapSizeLimit?: number;
  totalJSHeapSize?: number;
  usedJSHeapSize?: number;
}

interface PerformanceSnapshot {
  memory?: PerformanceMemorySnapshot;
}

type ObservePainWindow = (
  options: CriticalWorkflowObservationOptions
) => boolean;
export interface AmbientResponsivenessSamplingOptions {
  intervalBaselineMs: number;
  observationDurationMs: number;
}

interface AmbientResponsivenessSamplingDependencies {
  observePainWindow?: ObservePainWindow;
  random?: () => number;
  getEnvironmentContext?: () => CriticalWorkflowContext;
  getPageState?: () => RumViewPageState;
}

const noop = () => {};
const observeAmbientPainWindow: ObservePainWindow = options =>
  !datadogEnabled() || observeCriticalWorkflowPainWindow(options);

export const startAmbientResponsivenessSampling = (
  options: AmbientResponsivenessSamplingOptions | undefined,
  {
    observePainWindow = observeAmbientPainWindow,
    random = Math.random,
    getEnvironmentContext = getAmbientResponsivenessEnvironmentContext,
    getPageState = getRumViewPageState,
  }: AmbientResponsivenessSamplingDependencies = {}
): (() => void) => {
  if (options == null || !isValidSamplingOptions(options)) {
    return noop;
  }

  let running = true;
  let timeoutId: number | undefined;

  const isDocumentVisible = () =>
    !document.hidden && document.visibilityState !== 'hidden';

  const clearScheduledWindow = () => {
    if (timeoutId != null) {
      window.clearTimeout(timeoutId);
      timeoutId = undefined;
    }
  };

  const handleVisibilityChange = () => {
    if (isDocumentVisible()) {
      scheduleNextWindow();
    } else {
      clearScheduledWindow();
    }
  };

  const stop = () => {
    if (!running) {
      return;
    }
    running = false;
    clearScheduledWindow();
    document.removeEventListener('visibilitychange', handleVisibilityChange);
  };

  const runObservation = () => {
    timeoutId = undefined;
    if (!running || !isDocumentVisible()) {
      return;
    }

    const pageStateAtStart = getPageState();
    if (pageStateAtStart !== 'active' && pageStateAtStart !== 'passive') {
      scheduleNextWindow();
      return;
    }

    const observed = observePainWindow({
      workflow: AMBIENT_RESPONSIVENESS_WORKFLOW,
      windowDurationMs: options.observationDurationMs,
      recordAbandoned: false,
      workflowContext: {
        ambientSamplerVersion: AMBIENT_RESPONSIVENESS_SAMPLER_VERSION,
        measurementMode: 'ambient-random',
        samplingIntervalBaselineMs: options.intervalBaselineMs,
        ...getEnvironmentContext(),
        pageStateAtStart,
      },
    });

    if (observed) {
      scheduleNextWindow();
    } else {
      stop();
    }
  };

  const scheduleNextWindow = () => {
    if (!running || !isDocumentVisible() || timeoutId != null) {
      return;
    }

    const randomUnit = Math.min(1 - Number.EPSILON, Math.max(0, random()));
    const randomizedGapMeanMs =
      options.intervalBaselineMs - options.observationDurationMs;
    const delay = Math.min(
      MAX_TIMER_DELAY_MS,
      options.observationDurationMs +
        Math.round(-Math.log1p(-randomUnit) * randomizedGapMeanMs)
    );
    timeoutId = window.setTimeout(runObservation, delay);
  };

  document.addEventListener('visibilitychange', handleVisibilityChange);
  scheduleNextWindow();
  return stop;
};

export function getAmbientResponsivenessEnvironmentContext(
  navigatorValue: NavigatorSnapshot = typeof navigator === 'undefined'
    ? {}
    : (navigator as Navigator & NavigatorSnapshot),
  performanceValue: PerformanceSnapshot = typeof performance === 'undefined'
    ? {}
    : (performance as Performance & PerformanceSnapshot)
): CriticalWorkflowContext {
  const connection = navigatorValue.connection;
  const memory = performanceValue.memory;
  const usedJSHeapSize = memory?.usedJSHeapSize;
  const totalJSHeapSize = memory?.totalJSHeapSize;
  const jsHeapSizeLimit = memory?.jsHeapSizeLimit;

  return Object.fromEntries(
    Object.entries({
      networkOnline: navigatorValue.onLine,
      networkEffectiveType: connection?.effectiveType,
      networkDownlinkMbps: connection?.downlink,
      networkConnectionType: connection?.type,
      networkRttMs: connection?.rtt,
      networkSaveData: connection?.saveData,
      deviceMemoryGiB: navigatorValue.deviceMemory,
      jsHeapUsedMiB:
        usedJSHeapSize === undefined
          ? undefined
          : usedJSHeapSize / BYTES_PER_MEBIBYTE,
      jsHeapAllocatedMiB:
        totalJSHeapSize === undefined
          ? undefined
          : totalJSHeapSize / BYTES_PER_MEBIBYTE,
      jsHeapLimitMiB:
        jsHeapSizeLimit === undefined
          ? undefined
          : jsHeapSizeLimit / BYTES_PER_MEBIBYTE,
      jsHeapUtilizationPercentage:
        usedJSHeapSize === undefined || jsHeapSizeLimit === undefined
          ? undefined
          : (usedJSHeapSize / jsHeapSizeLimit) * 100,
    }).filter(([, value]) => value !== undefined)
  ) as CriticalWorkflowContext;
}

export const getAmbientResponsivenessSamplingOptions = (
  configValue: object | undefined
): AmbientResponsivenessSamplingOptions | undefined => {
  if (configValue == null) {
    return undefined;
  }

  const config = configValue as Record<string, unknown>;
  if (config.enabled !== true) {
    return undefined;
  }

  if (
    !isFiniteNumberInRange(
      config.sample,
      0,
      MAX_PAIN_OBSERVATION_DURATION_MS / MILLISECONDS_PER_SECOND
    ) ||
    !isFiniteNumberInRange(
      config.window,
      0,
      MAX_TIMER_DELAY_MS / MILLISECONDS_PER_SECOND
    ) ||
    config.sample >= config.window
  ) {
    return undefined;
  }

  return {
    intervalBaselineMs: config.window * MILLISECONDS_PER_SECOND,
    observationDurationMs: config.sample * MILLISECONDS_PER_SECOND,
  };
};

export const initializeAmbientResponsivenessSampling = (
  options?: AmbientResponsivenessSamplingOptions
): (() => void) => {
  if (envIsDev || envIsTest || options == null) {
    return noop;
  }
  return startAmbientResponsivenessSampling(options);
};

function isValidSamplingOptions(
  options: AmbientResponsivenessSamplingOptions
): boolean {
  return (
    isFiniteNumberInRange(
      options.observationDurationMs,
      0,
      MAX_PAIN_OBSERVATION_DURATION_MS
    ) &&
    isFiniteNumberInRange(options.intervalBaselineMs, 0, MAX_TIMER_DELAY_MS) &&
    options.observationDurationMs < options.intervalBaselineMs
  );
}

function isFiniteNumberInRange(
  value: unknown,
  exclusiveMinimum: number,
  inclusiveMaximum: number
): value is number {
  return (
    typeof value === 'number' &&
    Number.isFinite(value) &&
    value > exclusiveMinimum &&
    value <= inclusiveMaximum
  );
}
